import React, { useState, useRef, useEffect } from 'react';
import { Bell, CheckCircle2 } from 'lucide-react';
import { useNotifications, Notification } from '../../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ 
  className = ''
}) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        onClick={toggleOpen}
        className="relative p-2 text-white/70 hover:text-white hover:bg-white/5 rounded-full transition-colors duration-200"
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
            <span className="text-[10px] font-medium text-white leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-80 max-h-[85vh] sm:w-96 rounded-xl bg-[#01020E] border border-white/10 shadow-2xl flex flex-col z-50 overflow-hidden"
          style={{ animation: "slideDown 0.2s ease-out forwards" }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
            <h3 className="text-white font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                onClick={() => markAllAsRead()}
                className="text-xs text-primary hover:text-primary-light transition-colors flex items-center"
              >
                <CheckCircle2 size={12} className="mr-1" />
                Mark all read
              </button>
            )}
          </div>
          
          <div className="overflow-y-auto overflow-x-hidden p-1 max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-white-medium/50 text-sm">
                No new notifications
              </div>
            ) : (
              <ul className="space-y-[2px]">
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    {notification.link ? (
                      <Link
                        to={notification.link}
                        onClick={() => handleNotificationClick(notification)}
                        className={`block p-3 rounded-lg transition-colors duration-200 ${
                          notification.isRead ? 'hover:bg-white/5 text-white/70' : 'bg-primary/5 hover:bg-primary/10 text-white'
                        }`}
                      >
                        <NotificationContent notification={notification} />
                      </Link>
                    ) : (
                      <div
                        onClick={() => handleNotificationClick(notification)}
                        className={`block p-3 rounded-lg transition-colors cursor-pointer duration-200 ${
                          notification.isRead ? 'hover:bg-white/5 text-white/70' : 'bg-primary/5 hover:bg-primary/10 text-white'
                        }`}
                      >
                        <NotificationContent notification={notification} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Extracted sub-component for rendering the content
const NotificationContent = ({ notification }: { notification: Notification }) => (
  <div className="flex items-start gap-3">
    <div className={`mt-0.5 rounded-full p-1.5 flex-shrink-0 ${notification.isRead ? 'bg-white/10 text-white/50' : 'bg-primary/20 text-primary'}`}>
      <Bell size={14} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium mb-0.5 break-words">{notification.title}</p>
      <p className="text-xs opacity-80 leading-snug line-clamp-2 mb-1">{notification.content}</p>
      <p className="text-[10px] opacity-60">
        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
      </p>
    </div>
    {!notification.isRead && (
      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
    )}
  </div>
);

export default NotificationBell; 